import ApplicantInfo from '../../../../components/credit/application/ApplicantInfo/ApplicantInfo';
import BusinessCoAppIncome from '../../../../components/credit/application/BusinessCoApp/BusinessCoAppIncome';
import BusinessCoAppInformation from '../../../../components/credit/application/BusinessCoApp/BusinessCoAppInformation';
import BusinessInfo from '../../../../components/credit/application/BusinessInfo/BusinessInfo';
import { ComboBox } from '../../../../components/credit/ui/comboBox/ComboBox';
import { CreditApplicationType } from 'src/utils/enums/financialTypes.enum';
import { CreditAppService } from '../../../../services/credit-app.service';
import DialogModal from '../../../../components/ui/dialogModal';
import FinancialInfo from '../../../../components/credit/application/FinancialInfo/FinancialInfo';
import FooterPrivacy from 'src/views/auth/components/baseTemplate/FooterPrivacy';
import { FULL_NAME } from 'src/config';
import IndividualOccupationIncome from 'src/components/credit/application/IndividualOccupationIncome/IndividualOccupationIncome';
import { IUser } from '../../../../utils/interfaces/user';
import OtherInformation from '../../../../components/credit/application/OtherInformation/OtherInformation';
import Prompt from '../../../../components/ui/NavigationPrompt';
import TooltipIcon from '../../../../components/ui/tooltip';
import { useCreditForm } from 'src/customHooks/useCreditForm';
import { useLocalization } from 'src/utils/useLocalization';
import { useNavigate } from 'react-router-dom';
import { useStopwatch } from 'react-timer-hook';
import { useTranslation } from 'react-i18next';
import { v4 as uuidV4 } from 'uuid';
import VehicleInfo from '../../../../components/credit/application/VehicleInformation/VehicleInfo';
import {
    isSiteCanadian,
    removesNullOrUndefined,
} from '../../../../utils/helpers';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './styles.scss';

const CERO = 0;
const ONE = 1;
const PERCENTAGE = 100;
const MAXTIME = 11;
const LANDING_SCROLL_DELAY = 100;

const Application = () => {
    const { creditApp, creditAppPrompt } = useLocalization();
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    enum CreditModalStatus {
        PROCESSING = 'PROCESSING',
        SUCCESS = 'SUCCESS',
        ERROR = 'ERROR',
        TYPECHANGE = 'TYPECHANGE',
        NAMECHANGE = 'NAMECHANGE',
    }

    const editCreditDetailData = useSelector((state: IUser) => {
        return state.creditDetailData;
    });
    const {
        isCreditFormTouched,
        markCreditFormTouched,
        markCreditFormUntouched,
    } = useCreditForm();
    const [isEditMode, setIsEditMode] = useState(!!editCreditDetailData);
    const hasFocusedApplicationType = useRef(false);

    let financialTypesOptions: Array<any> = [
        {
            value: CreditApplicationType.INDIVIDUAL,
            label: creditApp.TYPE_INDIVIDUAL,
        },
        { value: CreditApplicationType.JOINT, label: creditApp.TYPE_JOINT },
        { value: CreditApplicationType.COAPP, label: creditApp.TYPE_COAPP },
        {
            value: CreditApplicationType.BUSINESS,
            label: creditApp.TYPE_BUSINESS,
        },
        {
            value: CreditApplicationType.BUSINESS_COAPP,
            label: creditApp.TYPE_BUSINESSCOAPP,
        },
        {
            value: CreditApplicationType.BUSINESS_PG,
            label: creditApp.TYPE_BUSINESSGUARANTOR,
        },
    ];

    if (editCreditDetailData?.creditApplication?.CreditApplicationType) {
        switch (
            editCreditDetailData?.creditApplication?.CreditApplicationType
        ) {
            case CreditApplicationType.INDIVIDUAL:
            case CreditApplicationType.JOINT:
            case CreditApplicationType.COAPP:
                financialTypesOptions = [
                    {
                        value: CreditApplicationType.INDIVIDUAL,
                        label: creditApp.TYPE_INDIVIDUAL,
                    },
                    {
                        value: CreditApplicationType.JOINT,
                        label: creditApp.TYPE_JOINT,
                    },
                    {
                        value: CreditApplicationType.COAPP,
                        label: creditApp.TYPE_COAPP,
                    },
                ];
                break;
            case CreditApplicationType.BUSINESS:
            case CreditApplicationType.BUSINESS_COAPP:
            case CreditApplicationType.BUSINESS_PG:
                financialTypesOptions = [
                    {
                        value: CreditApplicationType.BUSINESS,
                        label: creditApp.TYPE_BUSINESS,
                    },
                    {
                        value: CreditApplicationType.BUSINESS_COAPP,
                        label: creditApp.TYPE_BUSINESSCOAPP,
                    },
                    {
                        value: CreditApplicationType.BUSINESS_PG,
                        label: creditApp.TYPE_BUSINESSGUARANTOR,
                    },
                ];
                break;
        }
    }

    const initialAplicationType = () =>
        editCreditDetailData?.creditApplication?.CreditApplicationType ??
        CreditApplicationType.INDIVIDUAL;

    const [applicationType, setApplicationType] = useState<string>(
        initialAplicationType,
    );
    const [currentType, setCurrentType] = useState<string>(
        initialAplicationType,
    );

    const [vehicleType, setVehicleType] = useState<string>(
        editCreditDetailData?.creditApplication?.Vehicle?.VehicleType ?? 'New',
    );
    const [vehicleYear, setVehicleYear] = useState<string>('');
    const [vehicleMake, setVehicleMake] = useState<string>('');

    const [vehicleData, setVehicleData] = useState<Object>({});
    const [financialData, setFinancialData] = useState<Object>({});
    const [applicantData, setApplicantData] = useState<any>({});
    const [coapplicantData, setCoapplicantData] = useState<any>({});
    const [incomeData, setIncomeData] = useState<Object>({});
    const [coincomeData, setCoincomeData] = useState<Object>({});
    const [occupationData, setOccupationData] = useState<Object>({});
    const [cooccuptionData, setCooccupationData] = useState<Object>({});
    const [otherData, setOthernData] = useState<any>({});
    const [cootherData, setCootherData] = useState<any>({});
    const [businessData, setBusinessData] = useState<Object>({});

    const [formDisabled, setFormDisabled] = useState<boolean>(true);
    const [isPrintBtnClicked, setIsPrintBtnClicked] = useState<boolean>(false);

    const [vehicleValid, setVehicleValid] = useState<boolean>(false);
    const [applicantValid, setApplicantValid] = useState<boolean>(false);
    const [financeValid, setFinanceValid] = useState<boolean>(false);
    const [incomeValid, setIncomeValid] = useState<boolean>(false);
    const [otherValid, setOtherValid] = useState<boolean>(false);
    const [businessValid, setBusinessValid] = useState<boolean>(false);

    const [isVehicleOpen, setIsVehicleOpen] = useState<boolean>(false);
    const [isApplicantOpen, setIsApplicantOpen] = useState<boolean>(false);
    const [isFinanceOpen, setIsFinanceOpen] = useState<boolean>(false);
    const [isIncomeOpen, setIsIncomeOpen] = useState<boolean>(false);
    const [isOtherOpen, setIsOtherOpen] = useState<boolean>(false);
    const [isBusinessOpen, setIsBusinessOpen] = useState<boolean>(false);

    const [isVehicleBlocked, setIsVehicleBlocked] = useState<boolean>(true);
    const [isApplicantBlocked, setIsApplicantBlocked] = useState<boolean>(
        !isEditMode,
    );
    const [isFinanceBlocked, setIsFinanceBlocked] = useState<boolean>(
        !isEditMode,
    );
    const [isIncomeBlocked, setIsIncomeBlocked] = useState<boolean>(
        !isEditMode,
    );
    const [isOtherBlocked, setIsOtherBlocked] = useState<boolean>(!isEditMode);
    const [isBusinessBlocked, setIsBusinessBlocked] = useState<boolean>(
        !isEditMode,
    );

    useEffect(() => {
        if (isEditMode || hasFocusedApplicationType.current) return;

        const timeoutId = window.setTimeout(() => {
            const applicationTypeField = document.querySelector<HTMLElement>(
                '#type[role="combobox"]',
            );
            const applicationTitle = document.querySelector<HTMLElement>(
                '.credit-wrapper .c-section-heading',
            );

            applicationTypeField?.focus({ preventScroll: true });

            if (applicationTitle) {
                const titleTop =
                    applicationTitle.getBoundingClientRect().top +
                    window.scrollY;
                const topBarHeight =
                    document
                        .querySelector('.c-section-nav')
                        ?.getBoundingClientRect().height ?? Number('0');

                window.scrollTo({
                    top: Math.max(Number('0'), titleTop - topBarHeight),
                    behavior: 'auto',
                });
            }

            hasFocusedApplicationType.current = true;
        }, LANDING_SCROLL_DELAY);

        return () => window.clearTimeout(timeoutId);
    }, [isEditMode]);

    const [modalStatus, setModalStatus] = useState<string>('');
    const [percentageProgress, setPercentageProgress] = useState<number>(CERO);
    const [uniqueId, setUniqueId] = useState(CERO);

    const [isResubmit, setIsResubmit] = useState<boolean>(true);

    const [isPrimaryApplicantQuebec, setIsPrimaryApplicantQuebec] =
        useState<boolean>(false);
    const [isSecondaryApplicantQuebec, setIsSecondaryApplicantQuebec] =
        useState<boolean>(false);

    const { totalSeconds, start, reset, pause } = useStopwatch({
        autoStart: false,
    });

    const resetComponent = () => {
        setModalStatus('');
        setApplicationType(CreditApplicationType.INDIVIDUAL);
        setCurrentType(CreditApplicationType.INDIVIDUAL);
        resetToInitialForms();
        setIsVehicleBlocked(false);
        setIsVehicleOpen(true);
        setIsFinanceBlocked(true);
        setIsFinanceOpen(false);
        setIsApplicantBlocked(true);
        setIsApplicantOpen(false);
        setIsPrintBtnClicked(false);
        setUniqueId(uniqueId + ONE);
    };

    const executeTypeChange = () => {
        setCurrentType(applicationType);
        setModalStatus('');
        setIsVehicleOpen(true);
        setVehicleValid(false);
        setIsVehicleBlocked(false);
        setIsResubmit(false);
        setIsPrintBtnClicked(false);
        setUniqueId(uniqueId + ONE);
        resetToInitialForms();
        setIsEditMode(false);
    };

    const undoTypeChange = () => {
        setApplicationType(currentType);
        setModalStatus('');
    };

    const resetToInitialForms = () => {
        setIsVehicleBlocked(true);
        setIsFinanceBlocked(true);
        setIsApplicantBlocked(true);
        setIsIncomeBlocked(true);
        setIsOtherBlocked(true);
        setIsBusinessBlocked(true);
        setIsVehicleOpen(false);
        setIsApplicantOpen(false);
        setIsFinanceOpen(false);
        setIsIncomeOpen(false);
        setIsOtherOpen(false);
        setIsBusinessOpen(false);
        setVehicleType('New');
        setVehicleYear('');
        setVehicleMake('');
        setVehicleData({});
        setFinancialData({});
        setApplicantData({});
        setCoapplicantData({});
        setIncomeData({});
        setCoincomeData({});
        setOccupationData({});
        setCooccupationData({});
        setOthernData({});
        setCootherData({});
        setFormDisabled(true);
        setVehicleValid(false);
        setApplicantValid(false);
        setFinanceValid(false);
        setIncomeValid(false);
        setOtherValid(false);
        setBusinessValid(false);
        setIsResubmit(false);
    };

    const dealer = useSelector((state: IUser) => {
        return state.dealerDataInfo?.dealer;
    });

    const fullName = useSelector((state: IUser) => {
        return state.fullName;
    });

    useEffect(() => {
        if (modalStatus === CreditModalStatus.PROCESSING) {
            if (totalSeconds == CERO) {
                setPercentageProgress(CERO);
            }
            if (totalSeconds < MAXTIME) {
                setPercentageProgress(
                    Math.round((totalSeconds * PERCENTAGE) / MAXTIME),
                );
            } else {
                if (percentageProgress < PERCENTAGE) {
                    setPercentageProgress(percentageProgress + ONE);
                }
            }
        }
    }, [totalSeconds]);

    useEffect(() => {
        if (currentType != '') {
            setIsVehicleOpen(!isEditMode);
            setIsVehicleBlocked(false);
            if (
                isEditMode &&
                (vehicleValid ||
                    financeValid ||
                    applicantValid ||
                    incomeValid ||
                    otherValid ||
                    businessValid)
            ) {
                setFormDisabled(false);
                setIsPrintBtnClicked(false);
            }
            if (vehicleValid) {
                setIsVehicleOpen(false);
                setIsFinanceBlocked(false);
                if (financeValid) {
                    setIsFinanceOpen(false);
                    if (isEditMode) {
                        setFormDisabled(false);
                        setIsPrintBtnClicked(false);
                    }
                    if (
                        currentType == CreditApplicationType.BUSINESS ||
                        currentType == CreditApplicationType.BUSINESS_COAPP ||
                        currentType == CreditApplicationType.BUSINESS_PG
                    ) {
                        setIsBusinessBlocked(false);
                        if (businessValid) {
                            setIsBusinessOpen(false);
                            if (
                                currentType ==
                                    CreditApplicationType.BUSINESS_COAPP ||
                                currentType == CreditApplicationType.BUSINESS_PG
                            ) {
                                setIsApplicantBlocked(false);
                                if (applicantValid) {
                                    setIsApplicantOpen(false);
                                    setIsIncomeBlocked(false);
                                    if (incomeValid) {
                                        setIsIncomeOpen(false);
                                        setIsOtherBlocked(false);
                                        if (otherValid) {
                                            setIsOtherOpen(false);
                                            setIsOtherBlocked(false);
                                            setFormDisabled(false);
                                        } else {
                                            if (
                                                !isEditMode ||
                                                (isEditMode && isOtherOpen)
                                            ) {
                                                setIsOtherOpen(true);
                                                setFormDisabled(true);
                                            }
                                        }
                                    } else {
                                        if (
                                            !isEditMode ||
                                            (isEditMode && isIncomeOpen)
                                        ) {
                                            setIsIncomeOpen(true);
                                            setFormDisabled(true);
                                        }
                                    }
                                } else {
                                    if (
                                        !isEditMode ||
                                        (isEditMode && isApplicantOpen)
                                    ) {
                                        setIsApplicantOpen(true);
                                        setFormDisabled(true);
                                    }
                                }
                            } else {
                                setIsOtherBlocked(false);
                                if (otherValid) {
                                    setIsOtherOpen(false);
                                    setIsOtherBlocked(false);
                                    setFormDisabled(false);
                                } else {
                                    if (
                                        !isEditMode ||
                                        (isEditMode && isOtherOpen)
                                    ) {
                                        setIsOtherOpen(true);
                                        setFormDisabled(true);
                                    }
                                }
                            }
                        } else {
                            if (!isEditMode || (isEditMode && isBusinessOpen)) {
                                setIsBusinessOpen(true);
                                setFormDisabled(true);
                            }
                        }
                    } else {
                        setIsApplicantBlocked(false);
                        if (applicantValid) {
                            setIsApplicantOpen(false);
                            setIsIncomeBlocked(false);
                            if (incomeValid) {
                                setIsIncomeOpen(false);
                                setIsOtherBlocked(false);
                                if (otherValid) {
                                    setIsOtherOpen(false);
                                    setIsOtherBlocked(false);
                                    setFormDisabled(false);
                                } else {
                                    if (
                                        !isEditMode ||
                                        (isEditMode && isOtherOpen)
                                    ) {
                                        setIsOtherOpen(true);
                                        setFormDisabled(true);
                                    }
                                }
                            } else {
                                if (
                                    !isEditMode ||
                                    (isEditMode && isIncomeOpen)
                                ) {
                                    setIsIncomeOpen(true);
                                    setFormDisabled(true);
                                }
                            }
                        } else {
                            if (
                                !isEditMode ||
                                (isEditMode && isApplicantOpen)
                            ) {
                                setIsApplicantOpen(true);
                                setFormDisabled(true);
                            }
                        }
                    }
                } else {
                    if (!isEditMode || (isEditMode && isFinanceOpen)) {
                        setFormDisabled(true);
                        setIsFinanceOpen(true);
                    }
                }
            } else {
                if (!isEditMode || (isEditMode && isVehicleOpen)) {
                    setFormDisabled(true);
                    setIsVehicleOpen(true);
                }
            }
        }
    }, [
        vehicleValid,
        applicantValid,
        financeValid,
        incomeValid,
        otherValid,
        businessValid,
        currentType,
    ]);

    useEffect(() => {
        if (applicationType !== currentType) {
            if (
                editCreditDetailData?.creditApplication?.CreditApplicationType
            ) {
                setModalStatus(CreditModalStatus.TYPECHANGE);
            } else {
                executeTypeChange();
            }
        }
    }, [applicationType]);

    useEffect(() => {
        if (modalStatus !== CreditModalStatus.PROCESSING) {
            reset();
            pause();
        } else if (modalStatus === CreditModalStatus.PROCESSING) {
            start();
        }
    }, [modalStatus]);

    useEffect(() => {
        if (
            applicantData.residence &&
            applicantData.residence.current.address.state == 'QC'
        ) {
            setIsPrimaryApplicantQuebec(true);
        } else {
            setIsPrimaryApplicantQuebec(false);
            setOthernData((value: any) => {
                return {
                    ...value,
                    quebecAssessment: false,
                    quebecContract: false,
                };
            });
        }

        if (editCreditDetailData && applicantData.applicantName) {
            if (
                applicantData.applicantName.firstName !=
                    editCreditDetailData.creditApplication.PrimaryApplicant
                        .ApplicantName.FirstName ||
                applicantData.applicantName.lastName !=
                    editCreditDetailData.creditApplication.PrimaryApplicant
                        .ApplicantName.LastName ||
                (applicantData.applicantName.middleInitial !=
                    editCreditDetailData.creditApplication.PrimaryApplicant
                        .ApplicantName.MiddleInitial &&
                    editCreditDetailData.creditApplication.PrimaryApplicant
                        .ApplicantName.MiddleInitial != '')
            ) {
                setModalStatus('NAMECHANGE');
            }
        }
    }, [applicantData]);

    useEffect(() => {
        if (
            coapplicantData.residence &&
            coapplicantData.residence.current.address.state == 'QC'
        ) {
            setIsSecondaryApplicantQuebec(true);
        } else {
            setIsSecondaryApplicantQuebec(false);
            setCootherData((value: any) => {
                return {
                    ...value,
                    quebecAssessment: false,
                    quebecContract: false,
                };
            });
        }

        if (
            editCreditDetailData &&
            editCreditDetailData.creditApplication.JointOrCoApplicant &&
            coapplicantData.applicantName
        ) {
            if (
                coapplicantData.applicantName.firstName !=
                    editCreditDetailData.creditApplication.JointOrCoApplicant
                        .Applicant.ApplicantName.FirstName ||
                coapplicantData.applicantName.lastName !=
                    editCreditDetailData.creditApplication.JointOrCoApplicant
                        .Applicant.ApplicantName.LastName ||
                (coapplicantData.applicantName.middleInitial !=
                    editCreditDetailData.creditApplication.JointOrCoApplicant
                        .Applicant.ApplicantName.MiddleInitial &&
                    editCreditDetailData.creditApplication.JointOrCoApplicant
                        .Applicant.ApplicantName.MiddleInitial != '')
            ) {
                setModalStatus('NAMECHANGE');
            }
        }
    }, [coapplicantData]);

    useEffect(() => {
        if (
            (businessData as any).business &&
            (businessData as any).business?.province == 'QC'
        ) {
            setIsPrimaryApplicantQuebec(true);
        } else {
            setIsPrimaryApplicantQuebec(false);
            setOthernData((value: any) => {
                return {
                    ...value,
                    quebecAssessment: false,
                    quebecContract: false,
                };
            });
        }
    }, [businessData]);

    useEffect(
        () => () => {
            dispatch({
                type: 'DeleteCreditDetail',
            });
        },
        [],
    );

    async function submitCreditApp(applicationData: any) {
        setModalStatus(CreditModalStatus.PROCESSING);
        const service = new CreditAppService();
        service
            .submitApplicaton(applicationData)
            .then((response) => {
                if (response.data) {
                    markCreditFormUntouched();
                    setModalStatus(CreditModalStatus.SUCCESS);
                }
            })
            .catch(() => {
                setModalStatus(CreditModalStatus.ERROR);
            });
    }

    const submitApplication = () => {
        submitCreditApp(getApplicationDataObject());
    };

    function getApplicationDataObject(isPrint?: boolean) {
        const applicationCountry =
            dealer?.country == 'Canada' || dealer?.country == 'CA'
                ? 'CA'
                : 'US';
        const uuid = uuidV4().toUpperCase();
        const languageCountry = isSiteCanadian() ? 'CA' : 'US';

        const BEGINING = 0;
        const END = 2;
        const languageIdiom = i18n.language.slice(BEGINING, END);

        const copy = { ...otherData };
        const miscellaneousNotes = copy['notes'];
        delete copy.notes;

        const payloadId =
            editCreditDetailData && isResubmit
                ? '' + editCreditDetailData.applicationNumber
                : uuid;

        const applicationData = {
            id: payloadId,
            requestId: payloadId,
            submittedDateTime: new Date(),
            brand: dealer?.brand.toUpperCase(),
            contractCountry: applicationCountry,
            applicationType: currentType,
            preferredLanguage: `${languageIdiom}-${languageCountry}`,
            correlationId: payloadId,
            submittedBy: fullName || sessionStorage.getItem(FULL_NAME),
            sourceSystem: 'FSPortal',
            dealer: {
                brand: dealer?.brand.toUpperCase(),
                dealerName: dealer?.dealerName,
                dealerNumber: dealer?.dealerNumber,
                telephoneNumber: dealer?.phoneNumber,
                address: {
                    addressLine1: dealer?.addressLine1,
                    city: dealer?.city,
                    state: dealer?.stateProvince,
                    country: applicationCountry,
                    zipOrPostalCode: dealer?.postalCode.code,
                },
            },
            salesContact: {
                firstName: 'ONLINECA',
                lastName: 'FINANCE',
            },
            financing: { ...financialData, miscellaneousNotes },
            creditVehicle: vehicleData,
            organizationalApplicant: undefined,
        };

        if (isResubmit && editCreditDetailData) {
            (applicationData as any).resubmit = isResubmit;
        }

        if (
            currentType == CreditApplicationType.INDIVIDUAL ||
            currentType == CreditApplicationType.JOINT ||
            currentType == CreditApplicationType.COAPP
        ) {
            const primaryApplicantOccupation = { ...occupationData };

            (applicationData as any).primaryApplicant = {
                ...applicantData,
                employer: primaryApplicantOccupation,
                incomes: incomeData,
                ...copy,
            };
        }

        if (
            currentType == CreditApplicationType.JOINT ||
            currentType == CreditApplicationType.COAPP
        ) {
            const coApplicantOccupation = { ...cooccuptionData };

            (applicationData as any).coApplicant = {
                ...coapplicantData,
                employer: coApplicantOccupation,
                incomes: coincomeData,
                ...cootherData,
            };
        }

        if (
            currentType == CreditApplicationType.BUSINESS ||
            currentType == CreditApplicationType.BUSINESS_COAPP ||
            currentType == CreditApplicationType.BUSINESS_PG
        ) {
            const { applicant, business } = businessData as any;
            (applicationData as any).organizationalApplicant = {
                name: applicant.name,
                dayTimePhoneNumber: business.telephone,
                //emailAddressBusiness: 'test5@vwcredit.com',
                ...(applicationCountry === 'US' &&
                applicant.identificationNumber
                    ? {
                          identificationNumber:
                              applicant.identificationNumber.replaceAll(
                                  '-',
                                  '',
                              ),
                      }
                    : {}),
                residence: {
                    current: {
                        address: {
                            addressLine1: business.addressLine1,
                            addressLine2:
                                business.addressLine2 !== ''
                                    ? business.addressLine2
                                    : ' ',
                            city: business.city,
                            state: business.province,
                            country: applicationCountry,
                            zipOrPostalCode: business.postalCode,
                        },
                        timeAtAddress: {
                            timeAtAddrMonths: Number('0'),
                            timeAtAddrYears: Number('0'),
                        },
                        typeOfResidence: 'Business',
                    },
                },
                yearsInBusiness: applicant.years.toString() || '0',
                businessPipedaConsent: otherData.primaryPipedaConsent,
                businessCaslConsent: otherData.primaryCaslConsent,
                //backend still not accepting Quebec consents for business
                // quebecAssessment: otherData.quebecAssessment,
                // quebecContract: otherData.quebecContract,
            };
            if (
                otherData.quebecAssessment === true &&
                otherData.quebecContract === true
            ) {
                (
                    applicationData as any
                ).organizationalApplicant.quebecAssessment = true;
                (
                    applicationData as any
                ).organizationalApplicant.quebecContract = true;
            }
            if (isPrint) {
                applicationData.organizationalApplicant = setPrintBusinessObj(
                    applicationData.organizationalApplicant,
                    businessData,
                );
            }
        }

        if (
            currentType == CreditApplicationType.BUSINESS_COAPP ||
            currentType == CreditApplicationType.BUSINESS_PG
        ) {
            (applicationData as any).coApplicant = {
                ...coapplicantData,
                employer: occupationData,
                incomes: [...(incomeData as any)],
                primaryPipedaConsent: cootherData.primaryPipedaConsent,
                primaryCaslConsent: cootherData.primaryCaslConsent,
            };
            if (
                cootherData.quebecAssessment === true &&
                cootherData.quebecContract === true
            ) {
                (applicationData as any).coApplicant.quebecAssessment = true;
                (applicationData as any).coApplicant.quebecContract = true;
            }
            // delete (applicationData as any).coApplicant.employer.current
            //     .address;
            (
                applicationData as any
            ).coApplicant.residence.current.rentOrMortgagePymyt = (
                coapplicantData as any
            ).rentOrMortgagePymyt;

            if (currentType == CreditApplicationType.BUSINESS_COAPP) {
                applicationData.applicationType = 'BusinessCoapp';
            }

            if (currentType == CreditApplicationType.BUSINESS_PG) {
                applicationData.applicationType = 'BusinessGuarantor';
            }
        }

        return applicationData;
    }

    async function printApplication() {
        const newTab = window.open('', '_blank');
        if (!newTab) return false;
        const language = i18n.language.includes('fr') ? 'fr-CA' : 'en-CA';
        const applicationData = {
            ...getApplicationDataObject(true),
            requestId: undefined,
            id: undefined,
            preferredLanguage: language,
        };

        const service = new CreditAppService();
        let pdfUrl: string;
        try {
            const response = await service.creditApplicationPDF(
                applicationData,
            );
            pdfUrl = URL.createObjectURL(response.data);
            newTab.location.assign(pdfUrl);
            return true;
        } catch (error) {
            newTab.document.body.innerHTML =
                '<h1>Failed to load PDF</h1><p>Please try again later.</p>';
            return false;
        }
    }

    function setPrintBusinessObj(businessApplicant: any, businessData: any) {
        return {
            ...businessApplicant,
            businessType: businessData.applicant.status,
            incorporationYear:
                businessData.applicant.incorporation || undefined,
            owner: removesNullOrUndefined(businessData.owner),
            driver: removesNullOrUndefined(businessData.driver),
            financialReference: {
                ...removesNullOrUndefined(businessData.references),
                previous: undefined,
            },
        };
    }

    const disablePrintAndSubmitBtn = () => {
        setIsPrintBtnClicked(false);
        setFormDisabled(true);
    };

    return (
        <div key={uniqueId}>
            <Prompt
                show={isCreditFormTouched}
                beforeUnload={true}
                titleContent={creditAppPrompt.TITLE}
                bodyContent={creditAppPrompt.DESCRIPTION}
                cancelBtnLabel={creditAppPrompt.CANCEL_BTN_TXT}
                confirmBtnLabel={creditAppPrompt.PROCEED_BRN_TXT}
                onCancelHandler={() => {
                    //handle cancel
                }}
                onConfirmHandler={() => {
                    markCreditFormUntouched();
                    resetComponent();
                }}
                isContentCentered={false}
                showButtons={true}
                icon={''}
                isBigModal={false}
                isError={false}
                progress={undefined}
                modalExtraClass='credit-app-warning'
            />
            <DialogModal
                titleContent={
                    modalStatus === CreditModalStatus.PROCESSING
                        ? creditApp.MODAL_PROCESSING_TITLE
                        : modalStatus === CreditModalStatus.SUCCESS
                        ? creditApp.MODAL_SUCCESS_TITLE
                        : modalStatus === CreditModalStatus.TYPECHANGE
                        ? creditApp.MODAL_TYPE_TITLE
                        : modalStatus === CreditModalStatus.NAMECHANGE
                        ? creditApp.MODAL_NAME_TITLE
                        : modalStatus === CreditModalStatus.ERROR
                        ? creditApp.MODAL_ERROR_TITLE
                        : ''
                }
                bodyContent={
                    modalStatus === CreditModalStatus.PROCESSING
                        ? creditApp.MODAL_PROCESSING_SUBTITLE
                        : modalStatus === CreditModalStatus.SUCCESS
                        ? creditApp.MODAL_SUCCESS_SUBTITLE
                        : modalStatus === CreditModalStatus.TYPECHANGE
                        ? creditApp.MODAL_TYPE_SUBTITLE
                        : modalStatus === CreditModalStatus.NAMECHANGE
                        ? creditApp.MODAL_NAME_SUBTITLE
                        : modalStatus === CreditModalStatus.ERROR
                        ? creditApp.MODAL_ERROR_SUBTITLE
                        : ''
                }
                open={modalStatus === '' ? false : true}
                cancelBtnLabel={
                    modalStatus === CreditModalStatus.SUCCESS
                        ? creditApp.MODAL_SUCCESS_CANCEL
                        : modalStatus === CreditModalStatus.TYPECHANGE
                        ? creditApp.MODAL_TYPE_BACK
                        : modalStatus === CreditModalStatus.NAMECHANGE
                        ? creditApp.MODAL_NAME_CANCEL
                        : ''
                }
                confirmBtnLabel={
                    modalStatus === CreditModalStatus.SUCCESS
                        ? creditApp.MODAL_SUCCESS_NEXT
                        : modalStatus === CreditModalStatus.TYPECHANGE
                        ? creditApp.MODAL_TYPE_NEW
                        : modalStatus === CreditModalStatus.ERROR
                        ? creditApp.MODAL_ERROR_NEXT
                        : ''
                }
                onCancelHandler={
                    modalStatus === CreditModalStatus.SUCCESS
                        ? resetComponent
                        : modalStatus === CreditModalStatus.TYPECHANGE
                        ? undoTypeChange
                        : () => {
                              setModalStatus('');
                          }
                }
                onConfirmHandler={
                    modalStatus === CreditModalStatus.SUCCESS
                        ? () => {
                              navigate('/dashboard/credit/status');
                          }
                        : modalStatus === CreditModalStatus.TYPECHANGE
                        ? executeTypeChange
                        : () => {
                              setModalStatus('');
                          }
                }
                isContentCentered={true}
                showButtons={
                    modalStatus === CreditModalStatus.PROCESSING
                        ? false
                        : modalStatus === CreditModalStatus.SUCCESS
                        ? true
                        : modalStatus === CreditModalStatus.TYPECHANGE
                        ? true
                        : modalStatus === CreditModalStatus.NAMECHANGE
                        ? true
                        : modalStatus === CreditModalStatus.ERROR
                        ? true
                        : false
                }
                icon={
                    modalStatus === CreditModalStatus.SUCCESS
                        ? '[semantic-car]'
                        : modalStatus === CreditModalStatus.ERROR
                        ? '[semantic-warning]'
                        : ''
                }
                isBigModal={true}
                isError={modalStatus === CreditModalStatus.ERROR ? true : false}
                progress={
                    modalStatus === CreditModalStatus.PROCESSING
                        ? percentageProgress
                        : undefined
                }
                secondBodyContent={
                    modalStatus === CreditModalStatus.TYPECHANGE
                        ? creditApp.MODAL_TYPE_TEXT
                        : undefined
                }
                showConfirmButton={
                    modalStatus === CreditModalStatus.NAMECHANGE ? false : true
                }
                secondCancelBtnLabel={
                    modalStatus === CreditModalStatus.NAMECHANGE
                        ? creditApp.MODAL_NAME_CHANGE
                        : ''
                }
                onSecondCancelHandler={() => {
                    setIsResubmit(false);
                    setModalStatus('');
                }}
                thirdCancelBtnLabel={
                    modalStatus === CreditModalStatus.NAMECHANGE
                        ? creditApp.MODAL_NAME_CORRECT
                        : ''
                }
                onThirdCancelHandler={() => {
                    setIsResubmit(true);
                    setModalStatus('');
                }}
            />
            <div
                className={
                    'credit-app-wrapper-container dashboard-content-wrapper'
                }
            >
                <div className='credit-wrapper dashboard-content-container'>
                    <section className='o-content-section  '>
                        <div className='o-page-wrap  '>
                            <header className='c-section-heading '>
                                <h3 className='c-section-heading__title'>
                                    <span className='c-section-heading__title-text'>
                                        {isEditMode
                                            ? creditApp.UPDATE_TITLE
                                            : creditApp.TITLE}
                                    </span>
                                </h3>
                            </header>
                            <div className='global-selector'>
                                <div>
                                    <label
                                        htmlFor='m1lh7pdb'
                                        className='c-form-field__label'
                                    >
                                        {creditApp.TYPE_LABEL} &nbsp;
                                    </label>
                                    <TooltipIcon
                                        text={creditApp.TOOLTIP}
                                    ></TooltipIcon>
                                </div>
                                <ComboBox
                                    name={'type'}
                                    options={financialTypesOptions}
                                    defaultValue={
                                        financialTypesOptions[0].value
                                    }
                                    placeholder={creditApp.SELECT_OPTION}
                                    selectedOption={applicationType}
                                    onSelect={(type) => {
                                        markCreditFormTouched();
                                        setApplicationType(type);
                                    }}
                                    disabled={false}
                                />
                            </div>
                            <VehicleInfo
                                applicationType={currentType}
                                vehicleType={vehicleType}
                                setVehicleType={setVehicleType}
                                vehicleYear={vehicleYear}
                                setVehicleYear={setVehicleYear}
                                setVehicleData={setVehicleData}
                                setVehicleValid={setVehicleValid}
                                isOpen={isVehicleOpen}
                                setOpen={setIsVehicleOpen}
                                vehicleMake={vehicleMake}
                                setVehicleMake={setVehicleMake}
                                isBlocked={isVehicleBlocked}
                                initialVehicle={
                                    editCreditDetailData?.creditApplication
                                        ?.Vehicle ?? null
                                }
                                isEditMode={isEditMode}
                                disablePrintAndSubmitBtn={
                                    disablePrintAndSubmitBtn
                                }
                            ></VehicleInfo>
                            <FinancialInfo
                                year={+vehicleYear}
                                vehicleType={vehicleType}
                                setFinancialData={setFinancialData}
                                setFinanceValid={setFinanceValid}
                                isOpen={isFinanceOpen}
                                setOpen={setIsFinanceOpen}
                                make={vehicleMake}
                                isBlocked={isFinanceBlocked}
                                initialFinance={
                                    editCreditDetailData?.creditApplication
                                        ?.FinanceInformation ?? null
                                }
                                specialPrograms={
                                    editCreditDetailData?.creditApplication
                                        ?.SpecialPrograms?.value ?? null
                                }
                                isEditMode={isEditMode}
                                disablePrintAndSubmitBtn={
                                    disablePrintAndSubmitBtn
                                }
                            ></FinancialInfo>
                            {CreditApplicationType.INDIVIDUAL == currentType ||
                            CreditApplicationType.JOINT == currentType ||
                            CreditApplicationType.COAPP == currentType ? (
                                <>
                                    <ApplicantInfo
                                        applicationType={currentType}
                                        applicantData={applicantData}
                                        setApplicantData={setApplicantData}
                                        coapplicantData={coapplicantData}
                                        setCoapplicantData={setCoapplicantData}
                                        applicantValid={applicantValid}
                                        setApplicantValid={setApplicantValid}
                                        isOpen={isApplicantOpen}
                                        setOpen={setIsApplicantOpen}
                                        isBlocked={isApplicantBlocked}
                                        initialPrimaryApplicant={
                                            editCreditDetailData
                                                ?.creditApplication
                                                ?.PrimaryApplicant ?? null
                                        }
                                        initialJointOrCoApplicant={
                                            editCreditDetailData
                                                ?.creditApplication
                                                ?.JointOrCoApplicant
                                                ?.Applicant ?? null
                                        }
                                        initialJointOrCoAppRelationship={
                                            editCreditDetailData
                                                ?.creditApplication
                                                ?.JointOrCoApplicant
                                                ?.ApplicantRelationship ?? null
                                        }
                                        isEditMode={isEditMode}
                                        disablePrintAndSubmitBtn={
                                            disablePrintAndSubmitBtn
                                        }
                                    />
                                    <IndividualOccupationIncome
                                        applicationType={currentType}
                                        setIncomeData={setIncomeData}
                                        setCoincomeData={setCoincomeData}
                                        setOccupationData={setOccupationData}
                                        setCooccupationData={
                                            setCooccupationData
                                        }
                                        setIncomeValid={setIncomeValid}
                                        isOpen={isIncomeOpen}
                                        setOpen={setIsIncomeOpen}
                                        isBlocked={isIncomeBlocked}
                                        initialPrimaryApplicant={
                                            editCreditDetailData
                                                ?.creditApplication
                                                ?.PrimaryApplicant ?? null
                                        }
                                        initialJointOrCoApplicant={
                                            editCreditDetailData
                                                ?.creditApplication
                                                ?.JointOrCoApplicant
                                                ?.Applicant ?? null
                                        }
                                        isEditMode={isEditMode}
                                        disablePrintAndSubmitBtn={
                                            disablePrintAndSubmitBtn
                                        }
                                    />
                                </>
                            ) : null}
                            {currentType === CreditApplicationType.BUSINESS ||
                            currentType ===
                                CreditApplicationType.BUSINESS_COAPP ||
                            currentType ===
                                CreditApplicationType.BUSINESS_PG ? (
                                <>
                                    <BusinessInfo
                                        applicationType={currentType}
                                        isOpen={isBusinessOpen}
                                        setBusinessData={setBusinessData}
                                        setBusinessValid={setBusinessValid}
                                        setOpen={setIsBusinessOpen}
                                        isBlocked={isBusinessBlocked}
                                        initialBusinessApp={
                                            editCreditDetailData
                                                ?.creditApplication
                                                ?.BusinessApplicant ?? null
                                        }
                                        isEditMode={isEditMode}
                                        disablePrintAndSubmitBtn={
                                            disablePrintAndSubmitBtn
                                        }
                                    />
                                    {currentType ===
                                        CreditApplicationType.BUSINESS_COAPP ||
                                    currentType ===
                                        CreditApplicationType.BUSINESS_PG ? (
                                        <>
                                            <BusinessCoAppInformation
                                                applicationType={currentType}
                                                applicantData={applicantData}
                                                setApplicantData={
                                                    setApplicantData
                                                }
                                                coapplicantData={
                                                    coapplicantData
                                                }
                                                setCoapplicantData={
                                                    setCoapplicantData
                                                }
                                                setApplicantValid={
                                                    setApplicantValid
                                                }
                                                isOpen={isApplicantOpen}
                                                setOpen={setIsApplicantOpen}
                                                isBlocked={isApplicantBlocked}
                                                initialJointOrCoApplicant={
                                                    editCreditDetailData
                                                        ?.creditApplication
                                                        ?.JointOrCoApplicant
                                                        ?.Applicant ?? null
                                                }
                                                initialJointOrCoAppRelationship={
                                                    editCreditDetailData
                                                        ?.creditApplication
                                                        ?.JointOrCoApplicant
                                                        ?.ApplicantRelationship ??
                                                    null
                                                }
                                                isEditMode={isEditMode}
                                                disablePrintAndSubmitBtn={
                                                    disablePrintAndSubmitBtn
                                                }
                                            />
                                            <BusinessCoAppIncome
                                                applicationType={currentType}
                                                setIncomeData={setIncomeData}
                                                setOccupationData={
                                                    setOccupationData
                                                }
                                                setIncomeValid={setIncomeValid}
                                                isOpen={isIncomeOpen}
                                                setOpen={setIsIncomeOpen}
                                                isBlocked={isIncomeBlocked}
                                                initialJointOrCoApplicant={
                                                    editCreditDetailData
                                                        ?.creditApplication
                                                        ?.JointOrCoApplicant
                                                        ?.Applicant ?? null
                                                }
                                                isEditMode={isEditMode}
                                                disablePrintAndSubmitBtn={
                                                    disablePrintAndSubmitBtn
                                                }
                                            />
                                        </>
                                    ) : null}
                                </>
                            ) : null}
                            <OtherInformation
                                applicationType={currentType}
                                setOthernData={setOthernData}
                                setCootherData={setCootherData}
                                setOtherValid={setOtherValid}
                                isOpen={isOtherOpen}
                                setOpen={setIsOtherOpen}
                                isBlocked={isOtherBlocked}
                                initialPrimaryApplicant={
                                    editCreditDetailData?.creditApplication
                                        ?.PrimaryApplicant ?? null
                                }
                                initialJointOrCoApplicant={
                                    editCreditDetailData?.creditApplication
                                        ?.JointOrCoApplicant?.Applicant ?? null
                                }
                                initialNotes={
                                    editCreditDetailData?.creditApplication
                                        ?.Notes ?? null
                                }
                                initialBusinessApp={
                                    editCreditDetailData?.creditApplication
                                        ?.BusinessApplicant ?? null
                                }
                                isPrimaryQuebec={isPrimaryApplicantQuebec}
                                isSecondaryQuebec={isSecondaryApplicantQuebec}
                                isEditMode={isEditMode}
                                disablePrintAndSubmitBtn={
                                    disablePrintAndSubmitBtn
                                }
                            ></OtherInformation>
                            <div className='o-fieldset__row action-wrapper'>
                                <button
                                    className={`c-btn print-btn ${
                                        isPrintBtnClicked ? 'secondary-btn' : ''
                                    }`}
                                    type='button'
                                    disabled={formDisabled}
                                    onClick={() => {
                                        setIsPrintBtnClicked(true);
                                        printApplication();
                                    }}
                                >
                                    <span className='c-btn__text'>
                                        {creditApp.PRINT_BUTTON}
                                    </span>
                                </button>
                                <button
                                    className='c-btn'
                                    type='button'
                                    onClick={submitApplication}
                                    disabled={
                                        formDisabled || !isPrintBtnClicked
                                    }
                                >
                                    <span className='c-btn__text'>
                                        {isEditMode
                                            ? creditApp.SUBMIT_UPDATES_BUTTON
                                            : creditApp.SUBMIT_BUTTON}
                                    </span>
                                </button>
                            </div>
                            <div className='privacy-footer-container '>
                                <FooterPrivacy />
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};
export default Application;
